// MorrowOS — the browser connections a person actually has.
//
// A second pairing used to be invisible: another machine, a reinstalled extension, or a profile
// paired months ago all showed up as the single word "connected". When commands went to one of
// those instead of the browser in front of you, it looked like the integration was broken —
// "it says it is doing things but nothing opens". So: show every connection, say which one is
// DRIVING, and let it be chosen, named or dropped.
//
// Backed by /api/morrow/browser/sessions{,/primary,/rename,/forget,/forget-all}.
import { Loader2Icon, MonitorIcon, StarIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { apiUrl } from "../../apiBase";
import { useI18n } from "~/i18n";
import { Button } from "../ui/button";
import { InlineRename } from "../workflows/InlineRename";

type BrowserSession = {
  readonly key: string;
  readonly pairedAt: number;
  readonly lastSeen: number;
  readonly online: boolean;
  /** The one commands are being sent to right now. */
  readonly active: boolean;
  /** Chosen by the person, as opposed to picked by the newest-pairing rule. */
  readonly chosen: boolean;
  readonly label: string | null;
};

/** "2 minutes ago" at the coarseness this list needs — exact seconds would just flicker. */
function ago(at: number, t: ReturnType<typeof useI18n>["t"]): string {
  if (!at) return t("browser.sessions.never");
  const mins = Math.floor((Date.now() - at) / 60_000);
  if (mins < 1) return t("browser.sessions.justNow");
  if (mins < 60) return t("browser.sessions.minutesAgo", { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("browser.sessions.hoursAgo", { count: hours });
  return t("browser.sessions.daysAgo", { count: Math.floor(hours / 24) });
}

export function BrowserConnectionsList({ userId }: { userId: string | null }) {
  const { t } = useI18n();
  const [sessions, setSessions] = useState<readonly BrowserSession[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(
        apiUrl(`/api/morrow/browser/sessions?userId=${encodeURIComponent(userId)}`),
        { headers: { accept: "application/json" } },
      );
      if (!res.ok) return;
      const data = (await res.json()) as { sessions?: BrowserSession[] };
      setSessions(data.sessions ?? []);
    } catch {
      // Leave the last known list on screen: a blip is not "you have no browsers".
    } finally {
      setLoaded(true);
    }
  }, [userId]);

  useEffect(() => {
    void load();
    // Sessions go stale on their own (a browser stops polling), so this re-reads rather than
    // waiting for the person to navigate away and back.
    const timer = setInterval(() => void load(), 10_000);
    return () => clearInterval(timer);
  }, [load]);

  const act = useCallback(
    async (path: string, body: Record<string, unknown>, tag: string) => {
      if (!userId) return;
      setBusy(tag);
      try {
        const res = await fetch(apiUrl(path), {
          method: "POST",
          headers: { "content-type": "application/json", accept: "application/json" },
          body: JSON.stringify({ userId, ...body }),
        });
        const data = (await res.json().catch(() => null)) as { sessions?: BrowserSession[] } | null;
        if (data?.sessions) setSessions(data.sessions);
        else await load();
      } finally {
        setBusy(null);
      }
    },
    [userId, load],
  );

  // Nothing has ever paired: the page above already explains how, so stay quiet.
  if (!loaded || sessions.length === 0) return null;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-foreground">{t("browser.sessions.title")}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {sessions.length > 1
              ? t("browser.sessions.subtitleMany")
              : t("browser.sessions.subtitleOne")}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy !== null}
          onClick={() => void act("/api/morrow/browser/sessions/forget-all", {}, "all")}
        >
          {busy === "all" ? <Loader2Icon className="size-4 animate-spin" /> : null}
          {t("browser.sessions.disconnectAll")}
        </Button>
      </div>

      <ul className="flex flex-col gap-2">
        {sessions.map((sess) => (
          <li
            key={sess.key}
            className="flex items-center gap-3 rounded-lg border border-border/60 bg-background/40 p-3"
          >
            <span
              className={`flex size-8 shrink-0 items-center justify-center rounded-lg border ${
                sess.active
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-500"
                  : "border-border/60 bg-muted/40 text-muted-foreground"
              }`}
            >
              <MonitorIcon className="size-4" />
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
                <InlineRename
                  value={sess.label ?? t("browser.sessions.unnamed")}
                  onRename={async (next) => {
                    await act(
                      "/api/morrow/browser/sessions/rename",
                      { key: sess.key, label: next },
                      `rename:${sess.key}`,
                    );
                    return true;
                  }}
                />
                {sess.active ? (
                  <span className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-500">
                    {t("browser.sessions.driving")}
                  </span>
                ) : null}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {sess.online ? t("browser.sessions.online") : t("browser.sessions.offline")} ·{" "}
                {t("browser.sessions.lastSeen", { when: ago(sess.lastSeen, t) })}
                {sess.chosen ? ` · ${t("browser.sessions.chosenByYou")}` : ""}
              </p>
            </div>

            {/* Making an OFFLINE browser primary is allowed on purpose: you may be about to
                open it, and the choice simply takes effect when it polls. */}
            <Button
              type="button"
              size="sm"
              variant={sess.chosen ? "outline" : "ghost"}
              disabled={busy !== null}
              title={t("browser.sessions.makePrimary")}
              onClick={() =>
                void act(
                  "/api/morrow/browser/sessions/primary",
                  { key: sess.chosen ? "" : sess.key },
                  `primary:${sess.key}`,
                )
              }
            >
              {busy === `primary:${sess.key}` ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : (
                <StarIcon className="size-3.5" />
              )}
              {sess.chosen ? t("browser.sessions.unsetPrimary") : t("browser.sessions.makePrimary")}
            </Button>

            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy !== null}
              aria-label={t("browser.sessions.disconnect")}
              title={t("browser.sessions.disconnect")}
              onClick={() =>
                void act(
                  "/api/morrow/browser/sessions/forget",
                  { key: sess.key },
                  `forget:${sess.key}`,
                )
              }
            >
              {busy === `forget:${sess.key}` ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : (
                <XIcon className="size-3.5" />
              )}
            </Button>
          </li>
        ))}
      </ul>

      <p className="text-[11px] text-muted-foreground/70">{t("browser.sessions.forgetNote")}</p>
    </div>
  );
}
